import {
  Rate,
  RatePost,
  RatePut,
  RatingTable,
  RatingTablePost,
  RatingTablePut,
} from "callable-data-objects";
import { APIRatingTableContext } from "Providers/APIContexts";
import { APIRatingTableProps, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";

const APIRatingTableProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const [ratingTables, setRatingTables] = useState<RatingTable[]>([]);
  const [ratingTable, setRatingTable] = useState<RatingTable | undefined>();
  const [rate, setRate] = useState<Rate | undefined>();
  const [create, setCreate] = useState<RatingTablePost | undefined>();
  const [createRate, setCreateRate] = useState<RatePost | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<undefined | string>("");
  const [searchRate, setSearchRate] = useState<undefined | string>("");

  async function list() {
    setLoading(true);
    const response = await api.ratingTableService.list();
    setRatingTables(response);
    setLoading(false);
  }

  useEffect(() => {
    list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  function select(id: string): void {
    setLoading(true);
    api.ratingTableService.get(id).then((resp: RatingTable) => {
      setRatingTable(resp);
      setLoading(false);
    });
  }

  function selectRate(id: string): void {
    if (ratingTable) {
      const r = ratingTable.rates.find((r) => r.id === id);
      setRate(r);
    }
  }

  function unselect() {
    setRatingTable(undefined);
    setRate(undefined);
  }

  function unselectRate() {
    setRate(undefined);
  }

  function createRatingTable() {
    setCreate({
      name: "",
      description: "",
      appliedAt: "termination",
      type: "customer",
    });
  }

  function createRatingTableRate() {
    setCreateRate({
      caller: "*",
      called: "",
      timeProfile: "a",
      billAfterDuration: 0,
      minDuration: 0,
      price: 0.0,
      setup: 0.0,
    });
  }

  function uncreate() {
    setCreate(undefined);
    setCreateRate(undefined);
  }

  function uncreateRate() {
    setCreateRate(undefined);
  }

  function validateRatingTableName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      ratingTables
        .filter((rt) => ratingTable && ratingTable.id !== rt.id)
        .map((rt) => rt.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  async function save(payload: RatingTablePost) {
    if (create) {
      const response = await api.ratingTableService.post("", payload);
      if (response) {
        list();
        select(response.id);
        uncreate();
      }
    }
  }

  async function saveRate(payload: RatePost) {
    if (createRate && ratingTable) {
      const response = await api.ratingTableService
        .rate(ratingTable.id)
        .post("", payload);
      if (response) {
        selectRate(response.id);
      }
    }
  }

  async function update(payload: RatingTablePut) {
    if (ratingTable) {
      await api.ratingTableService.put(payload.id, payload);
      list();
      select(payload.id);
    }
  }

  async function remove(id: string) {
    await api.ratingTableService.delete(id);
    list();
    unselect();
  }

  async function removeRate(id: string) {
    if (ratingTable) {
      ratingTable.rates = ratingTable.rates.filter((r) => r.id !== id);
      update(ratingTable);
    }
  }

  async function cloneRatingTable() {
    if (ratingTable) {
      const response = await api.ratingTableService.clone(ratingTable);
      list();
      if (response instanceof RatingTable) setRatingTable(response);
    }
  }

  async function updateRate(payload: RatePut) {
    if (ratingTable && rate) {
      await api.ratingTableService
        .rate(ratingTable.id)
        .put(payload.id, payload)
        .then(() => {
          setRate(undefined);
          select(ratingTable.id);
        });
    }
  }

  const ratingTableProps: APIRatingTableProps = {
    rate,
    ratingTables,
    ratingTable,
    create,
    createRate,
    loading,
    save,
    saveRate,
    update,
    updateRate,
    validateRatingTableName,
    select,
    selectRate,
    unselect,
    unselectRate,
    createRatingTable,
    createRatingTableRate,
    uncreate,
    uncreateRate,
    search,
    setSearch,
    searchRate,
    setSearchRate,
    cloneRatingTable,
    remove,
    removeRate,
  };

  return (
    <APIRatingTableContext.Provider value={ratingTableProps}>
      {children}
    </APIRatingTableContext.Provider>
  );
};

export default APIRatingTableProvider;
